import { useTranslation } from 'react-i18next';
import { Button, Flex, Icon, Text } from '@kognitos/lattice';

export interface IManifestUpgradeBannerProps {
  /** Sends the upgrade-request message to the agent's thread. */
  onUpgrade: () => void;
  /** True while the request is in flight — disables the button and shows a spinner. */
  isSending: boolean;
  /** True once the request has been sent this session — swaps the button for a confirmation. */
  hasRequested: boolean;
}

/**
 * Prompts the user to ask the agent to regenerate this automation's
 * structured step manifest — shown only for automations built before the
 * Steps view existed (real code, but no `code_manifest` on the record), so
 * the Steps/Markdown toggle has nothing to show and stays hidden.
 */
export function ManifestUpgradeBanner({
  onUpgrade,
  isSending,
  hasRequested,
}: IManifestUpgradeBannerProps) {
  const { t: tAutomation } = useTranslation('automation');

  return (
    <Flex
      align="center"
      justify="between"
      gap={3}
      width="full"
      paddingX={4}
      paddingY={3}
      className="min-w-0 shrink-0 rounded-t-xl border bg-card shadow-custom"
      data-testid="manifest-upgrade-banner"
    >
      <Flex align="center" gap={2} flex="1" className="min-w-0">
        <Icon
          type="Sparkles"
          size="sm"
          className="shrink-0 text-muted-foreground"
        />
        <Text level="small" weight="semibold" className="block w-full truncate">
          {tAutomation('automation.manifestSopView.upgradeBanner.title')}
        </Text>
      </Flex>
      {hasRequested ? (
        <Text level="small" color="muted" className="shrink-0">
          {tAutomation('automation.manifestSopView.upgradeBanner.requested')}
        </Text>
      ) : (
        <Button
          size="sm"
          variant="outline"
          className="shrink-0"
          onClick={onUpgrade}
          disabled={isSending}
          isLoading={isSending}
          data-testid="manifest-upgrade-banner-button"
        >
          {tAutomation('automation.manifestSopView.upgradeBanner.action')}
        </Button>
      )}
    </Flex>
  );
}
